import React from 'react';
import { X, Dumbbell, Clock } from 'lucide-react';
import { formatExerciseValue, getExerciseUnitLabel } from '../../utils/exerciseFormatters';
import { formatRecoveryDisplaySeconds } from '../../utils/formatRecoveryTime';
import { formatWorkoutRepsTarget } from '../../constants/workoutRepsMode';
import { formatWorkoutWeightTarget } from '../../constants/workoutSeriesPlan';
import { getWorkoutSetTypeLabel, getWorkoutSetTypeBadgeClass } from '../../constants/workoutSetTypes';

export default function WorkoutDetailModal({ workout, client, isOpen, onClose }) {
    if (!isOpen || !workout) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="flex max-h-[85vh] w-[80vw] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
                {/* Header */}
                <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
                    <div className="min-w-0 pr-2">
                        <h2 className="text-xl font-semibold text-gray-900 dark:font-open-sans dark:text-white">
                            {workout.nome}
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-white/75">
                            Cliente: {client.name}
                        </p>
                        {workout.descrizione && (
                            <p className="mt-1 text-sm text-gray-500 dark:text-white/60">
                                {workout.descrizione}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 rounded-lg p-2 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                    >
                        <X size={20} className="text-gray-600 dark:text-white/90" />
                    </button>
                </div>

                {/* Content */}
                <div className="max-h-[calc(85vh-120px)] overflow-y-auto bg-white p-6 dark:bg-gray-900">
                    {workout.esercizi && workout.esercizi.length > 0 ? (
                        <div className="space-y-4">
                            <div className="mb-6 flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 dark:font-open-sans dark:text-white">
                                    Esercizi ({workout.esercizi.length})
                                </h3>
                                <div className="text-sm text-gray-500 dark:text-white/70">
                                    Creata: {new Date(workout.data_creazione).toLocaleDateString('it-IT')}
                                </div>
                            </div>

                            {workout.esercizi.map((esercizio, index) => (
                                <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-gray-900">
                                    <div className="flex items-start gap-4">
                                        {/* Immagine Esercizio */}
                                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-800">
                                             {esercizio.immagine_nome ? (
                                                 <img
                                                     src={`/api/serve_image.php?filename=${encodeURIComponent(esercizio.immagine_nome)}`}
                                                     alt={esercizio.nome}
                                                     className="w-full h-full object-cover select-none"
                                                     onContextMenu={(e) => e.preventDefault()}
                                                     draggable={false}
                                                     onError={(e) => {
                                                         e.target.style.display = 'none';
                                                         e.target.nextSibling.style.display = 'flex';
                                                     }}
                                                 />
                                             ) : null}
                                            <div className="w-full h-full flex items-center justify-center" style={{display: esercizio.immagine_nome ? 'none' : 'flex'}}>
                                                <Dumbbell size={24} className="text-gray-400" />
                                            </div>
                                        </div>

                                        <div className="flex-1">
                                            {/* Nome e gruppo muscolare */}
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 dark:text-white">
                                                        {index + 1}. {esercizio.nome}
                                                    </h4>
                                                    <p className="text-sm text-gray-600 dark:text-white/75">
                                                        {esercizio.gruppo_muscolare}
                                                    </p>
                                                </div>
                                                
                                                {/* Badge tipo set */}
                                                <div className="flex gap-2">
                                                    {esercizio.set_type && esercizio.set_type !== 'normal' && (
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getWorkoutSetTypeBadgeClass(esercizio.set_type)}`}>
                                                            {getWorkoutSetTypeLabel(esercizio.set_type)}
                                                        </span>
                                                    )}
                                                    {esercizio.linked_to_previous == 1 && (
                                                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                                                            Collegato
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Parametri allenamento */}
                                            {(() => {
                                                const isCardio = esercizio.is_cardio === 1 || esercizio.is_cardio === true;
                                                const isIsometric = esercizio.is_isometric === 1 || esercizio.is_isometric === true ||
                                                                   esercizio.is_isometric_scheda === 1 || esercizio.is_isometric_scheda === true;
                                                
                                                // Usa le funzioni di formatting unificate
                                                const displayValue = (() => {
                                                    if (
                                                        esercizio.set_type === 'piramidale' ||
                                                        esercizio.reps_mode === 'range' ||
                                                        esercizio.reps_mode === 'amrap'
                                                    ) {
                                                        const repsTarget = formatWorkoutRepsTarget(esercizio);
                                                        if (repsTarget) return repsTarget;
                                                    }
                                                    return formatExerciseValue(esercizio.ripetizioni, esercizio);
                                                })();
                                                const displayLabel = getExerciseUnitLabel(esercizio);
                                                
                                                // Capitalizza prima lettera
                                                const capitalizedLabel = displayLabel.charAt(0).toUpperCase() + displayLabel.slice(1);
                                                
                                                return (
                                                    <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                                                        <div className="rounded-lg border border-gray-200 bg-white p-3 text-center dark:border-white/15 dark:bg-gray-800">
                                                            <p className="text-xs text-gray-500 dark:text-gray-300/90">Serie</p>
                                                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                                {esercizio.serie}
                                                            </p>
                                                        </div>
                                                        <div className="rounded-lg border border-gray-200 bg-white p-3 text-center dark:border-white/15 dark:bg-gray-800">
                                                            <p className="text-xs text-gray-500 dark:text-gray-300/90">{capitalizedLabel}</p>
                                                            <p className="text-lg font-bold text-gray-900 dark:text-white break-words leading-tight">
                                                                {displayValue}
                                                            </p>
                                                        </div>
                                                        <div className="rounded-lg border border-gray-200 bg-white p-3 text-center dark:border-white/15 dark:bg-gray-800">
                                                            <p className="text-xs text-gray-500 dark:text-gray-300/90">Peso</p>
                                                            <p className="text-lg font-bold text-gray-900 dark:text-white break-words leading-tight">
                                                                {formatWorkoutWeightTarget(esercizio)}
                                                            </p>
                                                        </div>
                                                        <div className="rounded-lg border border-gray-200 bg-white p-3 text-center dark:border-white/15 dark:bg-gray-800">
                                                            <p className="text-xs text-gray-500 dark:text-gray-300/90">Recupero</p>
                                                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                                <Clock size={16} className="mr-1 inline" />
                                                                {formatRecoveryDisplaySeconds(esercizio.tempo_recupero)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {/* Note */}
                                            {(() => {
                                                // Parsa note JSON o stringa legacy per estrarre testo trainer
                                                const parseNote = (note) => {
                                                    if (!note) return null;
                                                    if (typeof note === 'string' && note.trim().startsWith('{')) {
                                                        try {
                                                            const notesObj = JSON.parse(note);
                                                            return notesObj.trainer || notesObj.user || notesObj.system || null;
                                                        } catch (e) {
                                                            return note.trim();
                                                        }
                                                    }
                                                    if (typeof note === 'object' && note !== null) {
                                                        return note.trainer || note.user || note.system || null;
                                                    }
                                                    return note.trim() || null;
                                                };
                                                
                                                const trainerNote = parseNote(esercizio.note);
                                                return trainerNote ? (
                                                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                                                    <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                                                            <strong>Note:</strong> {trainerNote}
                                                    </p>
                                                </div>
                                                ) : null;
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="py-12 text-center">
                            <Dumbbell size={48} className="mx-auto mb-4 text-gray-400 dark:text-white/40" />
                            <p className="text-gray-500 dark:text-white/65">Nessun esercizio in questa scheda</p>
                        </div>
                    )}
                </div>

                {/* Footer — stessa fascia del titolo */}
                <div className="shrink-0 border-t border-gray-200 bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-xl bg-gray-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-gray-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                        >
                            Chiudi
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


